import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Banknote,
  CircleDot,
  ClipboardList,
  Clock,
  CreditCard,
  Download,
  Eye,
  FileText,
  Mail,
  MinusCircle,
  Printer,
  Receipt,
  Smartphone,
  Wallet,
  X,
} from 'lucide-react';
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { getShift, listExpenseCategories, listShifts, type ShiftDetail } from '@/services/admin';
import { formatDate, formatRub, orderStatusLabels, paymentMethodLabels } from '@/utils/format';

import { ExpenseModal } from './Expenses';
import { ShiftReportPdf } from './shifts/ShiftReportPdf';
import { RemovalActor, RemovalReason, WaiterCollectionsCard } from './shifts/WaiterShiftParts';

const sectionLabels: Record<string, string> = { KITCHEN: 'Кухня', BAR: 'Бар', NONE: 'Блюда' };
const statusColor: Record<string, string> = {
  NEW: 'bg-sky-50 text-sky-700 border-sky-200',
  IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
  READY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  COMPLETED: 'bg-slate-50 text-slate-600 border-slate-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function ShiftsPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['admin/shifts'], queryFn: () => listShifts() });
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin/shift', openId],
    queryFn: () => getShift(openId!),
    enabled: !!openId,
  });

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Смены</h1>
        <p className="text-sm text-slate-500">История и текущие смены</p>
      </header>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[780px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">№</th>
                <th className="text-left px-4 py-3">Кассир</th>
                <th className="text-left px-4 py-3">Открыта</th>
                <th className="text-left px-4 py-3">Закрыта</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="text-right px-4 py-3">Заказов</th>
                <th className="text-right px-4 py-3">Выручка</th>
                <th className="text-right px-4 py-3">Касса</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => (
                <tr key={s.id} className="table-row">
                  <td className="px-4 py-3 font-medium">#{s.number}</td>
                  <td className="px-4 py-3">{s.cashierName ?? s.cashier?.name ?? '—'}</td>
                  <td className="px-4 py-3">{formatDate(s.openedAt)}</td>
                  <td className="px-4 py-3">{s.closedAt ? formatDate(s.closedAt) : '—'}</td>
                  <td className="px-4 py-3">
                    {s.status === 'OPEN' ? <Badge tone="success">Открыта</Badge> : <Badge>Закрыта</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">{s.ordersCount}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatRub(s.totalRevenue)}</td>
                  <td
                    className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${
                      s.cashDiff == null ? 'text-slate-400' : s.cashDiff < 0 ? 'text-rose-600' : s.cashDiff > 0 ? 'text-amber-600' : 'text-emerald-600'
                    }`}
                    title="Расхождение по наличным на закрытии"
                  >
                    {s.cashDiff == null ? '—' : s.cashDiff === 0 ? 'сошлось' : `${s.cashDiff > 0 ? '+' : '−'}${formatRub(Math.abs(s.cashDiff))}`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost" onClick={() => setOpenId(s.id)}>
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr><td colSpan={9} className="text-center text-slate-400 py-8">Смен нет</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={!!openId} onClose={() => setOpenId(null)} title="Отчёт по смене" size="xl">
        {detailLoading || !detail ? <Spinner /> : <ShiftDetails detail={detail} />}
      </Modal>
    </div>
  );
}

function ShiftDetails({ detail }: { detail: ShiftDetail }) {
  const [expenseOpen, setExpenseOpen] = useState(false);
  const { data: categories } = useQuery({
    queryKey: ['admin/expense-categories'],
    queryFn: () => listExpenseCategories(),
    enabled: expenseOpen,
  });
  return (
    <div className="space-y-5 text-sm">
      {/* Заголовок + действия */}
      <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-slate-100">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            {detail.establishment?.name ?? 'Resstro SAS'}
          </div>
          <div className="text-xl font-semibold mt-1">
            Смена #{detail.number} ·{' '}
            {detail.status === 'OPEN' ? (
              <span className="text-emerald-600">открыта</span>
            ) : (
              <span className="text-slate-500">закрыта</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {formatDate(detail.openedAt)} — {detail.closedAt ? formatDate(detail.closedAt) : '—'}
            </span>
            <span>{formatDuration(detail.durationMin)}</span>
            {(detail.cashierName || detail.cashier) && (
              <span>
                · Кассир: {detail.cashierName ?? detail.cashier?.name ?? detail.cashier?.phone ?? '—'}
                {detail.cashierName && detail.cashier?.name && detail.cashier.name !== detail.cashierName
                  ? ` · учётка ${detail.cashier.name}`
                  : ''}
              </span>
            )}
          </div>
        </div>
        <ReportActions detail={detail} />
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Выручка" value={formatRub(detail.totals.revenue)} tone="success" />
        <Kpi label="Заказов" value={detail.totals.ordersCount.toString()} tone="brand" />
        <Kpi label="Средний чек" value={formatRub(detail.totals.avgCheck)} tone="warning" />
        <Kpi
          label="Оплачено"
          value={detail.totals.paidOrdersCount.toString()}
          sub={`${detail.totals.ordersCount ? Math.round((detail.totals.paidOrdersCount / detail.totals.ordersCount) * 100) : 0}% от заказов`}
          tone="slate"
        />
        <Kpi label="Скидки" value={formatRub(detail.totals.discountTotal)} tone="slate" />
        <Kpi
          label="Отменено"
          value={detail.totals.cancelledCount.toString()}
          sub={detail.totals.cancelledCount > 0 ? formatRub(detail.totals.cancelledTotal) : undefined}
          tone="rose"
        />
        <Kpi label="Подитог" value={formatRub(detail.totals.subtotal)} tone="slate" />
        <Kpi label="Длительность" value={formatDuration(detail.durationMin)} tone="slate" />
      </div>

      {/* Касса и движения — Z-отчёт */}
      <div className="card p-4 border border-slate-100">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Wallet size={16} className="text-emerald-600" />
          <span className="font-semibold">Касса (наличные)</span>
          <span className="text-xs text-slate-400">расчёт и сверка</span>
          <button
            type="button"
            className="btn-outline text-xs ml-auto"
            onClick={() => setExpenseOpen(true)}
            title={
              detail.status === 'CLOSED'
                ? 'Кассир взял деньги из ящика и не провёл расход — внесите его, расхождение пересчитается'
                : 'Расход из кассы этой смены'
            }
          >
            <MinusCircle size={14} />
            {detail.status === 'CLOSED' ? 'Внести забытый расход' : 'Расход из кассы'}
          </button>
        </div>

        {/* Формула */}
        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
          <div
            className="grid grid-cols-2 gap-3 text-sm md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
            style={
              {
                // 8 колонок формулы: открытие, приход, возвраты, закуп, расходы,
                // «должно быть», факт, расхождение. Закуп/расходы показываем всегда
                // (даже 0) — та же формула, что на экране смены в кассе (п.3).
                '--cols': 8,
              } as React.CSSProperties
            }
          >
            <CashRow label="Остаток на открытие" value={formatRub(detail.cash.opening)} />
            <CashRow
              label={`+ Приход налом${detail.cash.cashInCount ? ` · ${detail.cash.cashInCount}` : ''}`}
              value={formatRub(detail.cash.cashIn)}
              tone="pos"
            />
            <CashRow
              label={`− Возвраты${detail.cash.cashOutCount ? ` · ${detail.cash.cashOutCount}` : ''}`}
              value={detail.cash.cashOut > 0 ? `−${formatRub(detail.cash.cashOut)}` : formatRub(0)}
              tone={detail.cash.cashOut > 0 ? 'neg' : undefined}
            />
            <CashRow
              label={`− Закуп из кассы${detail.cash.cashPayoutsCount ? ` · ${detail.cash.cashPayoutsCount}` : ''}`}
              value={detail.cash.cashPayouts > 0 ? `−${formatRub(detail.cash.cashPayouts)}` : formatRub(0)}
              tone={detail.cash.cashPayouts > 0 ? 'neg' : undefined}
            />
            <CashRow
              label={`− Расходы из кассы${detail.cash.cashExpensesCount ? ` · ${detail.cash.cashExpensesCount}` : ''}`}
              value={(detail.cash.cashExpenses ?? 0) > 0 ? `−${formatRub(detail.cash.cashExpenses ?? 0)}` : formatRub(0)}
              tone={(detail.cash.cashExpenses ?? 0) > 0 ? 'neg' : undefined}
            />
            <CashRow label="= Должно быть в ящике" value={formatRub(detail.cash.expected)} strong />
            <CashRow
              label="Факт на закрытие"
              value={detail.cash.actual != null ? formatRub(detail.cash.actual) : '—'}
            />
            <CashRow
              label="Расхождение"
              value={
                detail.cash.diff == null
                  ? '—'
                  : detail.cash.diff === 0
                    ? '0 ₽'
                    : `${detail.cash.diff > 0 ? '+' : ''}${formatRub(detail.cash.diff)}`
              }
              tone={
                detail.cash.diff == null
                  ? undefined
                  : detail.cash.diff === 0
                    ? 'pos'
                    : detail.cash.diff > 0
                      ? 'warn'
                      : 'neg'
              }
              strong
            />
          </div>
        </div>

        {/* Подсказка про безнал */}
        <div className="mt-2 text-xs text-slate-500">
          Безналичные поступления:{' '}
          <span className="font-medium text-slate-700">
            {formatRub(detail.cash.nonCashIn)}
          </span>{' '}
          · {detail.cash.nonCashInCount} оплат (в физической кассе не участвуют)
        </div>

        {/* Лог движений */}
        <div className="mt-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2">
            <ClipboardList size={14} /> Движения по кассе ({detail.cashMovements.length})
          </div>
          {detail.cashMovements.length === 0 ? (
            <div className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-md">
              Движений нет
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-slate-100">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                    <th className="text-left px-2 py-2 w-16">Время</th>
                    <th className="text-left px-2 py-2 w-20">Заказ</th>
                    <th className="text-left px-2 py-2 w-20">Метод</th>
                    <th className="text-left px-2 py-2 w-24">Тип</th>
                    <th className="text-right px-2 py-2">Сумма</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {detail.cashMovements.map((m, idx) => (
                    <tr key={m.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                      <td className="px-2 py-1.5 text-slate-500">
                        {new Date(m.createdAt).toLocaleTimeString('ru-RU', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-2 py-1.5 font-medium">
                        {m.orderNumber != null ? (
                          `#${m.orderNumber}`
                        ) : (
                          <span className="text-slate-500 font-normal">{m.note ?? '—'}</span>
                        )}
                        {m.acceptedBy ? (
                          <div className="text-[10px] font-normal text-slate-400">принял {m.acceptedBy}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          {m.isCash ? <Banknote size={11} /> : <CreditCard size={11} />}
                          {paymentMethodLabels[m.method] ?? m.method}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {m.direction === 'IN' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <ArrowDownCircle size={11} /> Приход
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <ArrowUpCircle size={11} />
                            {m.kind === 'SUPPLY' ? 'Поставка' : m.kind === 'EXPENSE' ? 'Расход' : 'Возврат'}
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right font-semibold ${
                          m.direction === 'OUT' ? 'text-rose-700' : 'text-slate-900'
                        }`}
                      >
                        {m.direction === 'OUT' ? '−' : ''}
                        {formatRub(m.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <WaiterCollectionsCard rows={detail.waiterCollections} />

      {/* Выплаты из кассы (за что оплачено) */}
      {detail.payouts && detail.payouts.length > 0 && (
        <div className="card p-4 border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={16} className="text-rose-600" />
            <span className="font-semibold">Выплаты из кассы</span>
            <span className="text-xs text-slate-400">наличные за поставки</span>
            <span className="ml-auto text-sm font-semibold text-rose-600">
              −{formatRub(detail.cash.cashPayouts)}
            </span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <th className="text-left px-2 py-2 w-16">Время</th>
                  <th className="text-left px-2 py-2">Поставщик / состав</th>
                  <th className="text-right px-2 py-2 w-28">Сумма</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="px-2 py-1.5 text-slate-500 align-top">
                      {new Date(p.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{p.supplierName ?? 'Поставка'}</div>
                      <div className="text-slate-500">
                        {p.items.map((it) => `${it.name} ${it.quantity} ${it.unit}`).join(' · ') || '—'}
                        {p.comment ? <span className="text-slate-400"> · {p.comment}</span> : null}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-rose-700 align-top">
                      −{formatRub(p.paidAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Расходы, проведённые через кассу смены */}
      {detail.expenses && detail.expenses.length > 0 && (
        <div className="card p-4 border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={16} className="text-rose-600" />
            <span className="font-semibold">Расходы смены</span>
            <span className="text-xs text-slate-400">траты, проведённые через кассу</span>
            <span className="ml-auto text-sm font-semibold text-rose-600">
              −{formatRub((detail.cash.cashExpenses ?? 0) + (detail.cash.transferExpenses ?? 0))}
            </span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <th className="text-left px-2 py-2 w-16">Время</th>
                  <th className="text-left px-2 py-2">Статья / комментарий</th>
                  <th className="text-left px-2 py-2 w-32">Способ</th>
                  <th className="text-right px-2 py-2 w-28">Сумма</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.expenses.map((e) => (
                  <tr key={e.id}>
                    <td className="px-2 py-1.5 text-slate-500 align-top">
                      {new Date(e.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium flex flex-wrap items-center gap-1.5">
                        {e.category?.name ?? 'Без статьи'}
                        {e.addedAfterClose && <Badge tone="warning">внесён после закрытия</Badge>}
                      </div>
                      <div className="text-slate-500">
                        {e.comment ?? '—'}
                        {e.user?.name ? <span className="text-slate-400"> · {e.user.name}</span> : null}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 align-top">
                      {e.method === 'KASSA_CASH' ? 'Наличные из кассы' : 'Перевод с кассы'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-rose-700 align-top">
                      −{formatRub(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Снятые позиции: кассир нажал «−» или убрал строку до оплаты */}
      {detail.itemChanges && detail.itemChanges.length > 0 && (
        <div className="card p-4 border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <MinusCircle size={16} className="text-amber-600" />
            <span className="font-semibold">Снятые позиции</span>
            <span className="text-xs text-slate-400">убраны из заказов до оплаты</span>
            <span className="ml-auto text-sm font-semibold text-amber-700">
              {detail.totals.removedItemsQty ?? detail.itemChanges.length} шт ·{' '}
              {formatRub(detail.totals.removedItemsAmount ?? 0)}
            </span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <th className="text-left px-2 py-2 w-16">Время</th>
                  <th className="text-left px-2 py-2 w-20">Заказ</th>
                  <th className="text-left px-2 py-2">Позиция</th>
                  <th className="text-left px-2 py-2 w-20">Было → стало</th>
                  <th className="text-left px-2 py-2 w-36">Кто снял</th>
                  <th className="text-right px-2 py-2 w-28">Сумма</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.itemChanges.map((c) => (
                  <tr key={c.id}>
                    <td className="px-2 py-1.5 text-slate-500 align-top">
                      {new Date(c.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-2 py-1.5 font-medium align-top">
                      {c.orderNumber != null ? `#${c.orderNumber}` : '—'}
                      {c.tableNumber != null ? (
                        <span className="text-slate-400 font-normal"> · стол {c.tableNumber}</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{c.dishName}</div>
                      <div className="text-slate-400">
                        {c.kind === 'REMOVED' ? 'удалена из заказа' : 'уменьшено количество'}
                      </div>
                      <RemovalReason change={c} />
                    </td>
                    <td className="px-2 py-1.5 tabular-nums align-top">
                      {c.qtyBefore} → {c.qtyAfter}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 align-top">
                      <RemovalActor change={c} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-700 align-top">
                      −{formatRub(c.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[11px] text-slate-400">
            Журнал фиксирует уменьшение количества и удаление позиций из открытых заказов. Отменённые
            заказы целиком — в блоке «Отменено».
          </div>
        </div>
      )}

      {/* Источники / Статусы */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card p-4 border border-slate-100">
          <div className="font-semibold mb-3">Источники заказов</div>
          <div className="space-y-2">
            {detail.sources.map((src) => {
              const total = detail.sources.reduce((sum, x) => sum + x.revenue, 0);
              const share = total > 0 ? Math.round((src.revenue / total) * 100) : 0;
              return (
                <div key={src.source}>
                  <div className="flex justify-between text-sm">
                    <span className="inline-flex items-center gap-2 font-medium">
                      {src.source === 'APP' ? <Smartphone size={12} /> : <CircleDot size={12} />}
                      {src.source === 'CASH' ? 'Касса' : 'Приложение'}
                    </span>
                    <span>
                      {src.count} шт · {formatRub(src.revenue)}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${src.source === 'CASH' ? 'bg-brand-500' : 'bg-emerald-500'}`}
                      style={{ width: `${share}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card p-4 border border-slate-100">
          <div className="font-semibold mb-3">Статусы заказов</div>
          <div className="grid grid-cols-2 gap-2">
            {detail.statuses.map((st) => (
              <div
                key={st.status}
                className={`rounded-md border px-2.5 py-2 text-xs ${statusColor[st.status]}`}
              >
                <div className="font-medium">{orderStatusLabels[st.status] ?? st.status}</div>
                <div className="mt-1 flex justify-between text-sm font-semibold">
                  <span>{st.count}</span>
                  <span className="opacity-70">{formatRub(st.revenue)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Методы оплаты / По разделам */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card p-4 border border-slate-100">
          <div className="font-semibold mb-3">По методам оплаты</div>
          <div className="divide-y divide-slate-100">
            {detail.paymentBreakdown.filter((p) => p.amount > 0 || p.count > 0).length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">Оплат нет</div>
            ) : (
              detail.paymentBreakdown
                .filter((p) => p.amount > 0 || p.count > 0)
                .map((p) => (
                  <div key={p.method} className="flex justify-between py-1.5 text-sm">
                    <span>{paymentMethodLabels[p.method] ?? p.method}</span>
                    <span>
                      <span className="text-slate-400 mr-3">{p.count} шт</span>
                      <span className="font-medium">{formatRub(p.amount)}</span>
                    </span>
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="card p-4 border border-slate-100">
          <div className="font-semibold mb-3">По разделам</div>
          <div className="divide-y divide-slate-100">
            {/* sectionRows — разделы заведения как есть (включая «Кальянную»);
                для отчётов, снятых до обновления бэкенда, падаем на 3 ключа. */}
            {(detail.sectionRows
              ?? (Object.entries(detail.sections) as Array<[string, { quantity: number; total: number }]>)
                .map(([sec, v]) => ({ name: sectionLabels[sec] ?? sec, ...v }))
            ).map((row) => (
              <div key={row.name} className="flex justify-between py-1.5 text-sm">
                <span>{row.name}</span>
                <span>
                  <span className="text-slate-400 mr-3">{row.quantity} шт</span>
                  <span className="font-medium">{formatRub(row.total)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Почасовая активность */}
      {detail.hours.length > 0 && (
        <div className="card p-4 border border-slate-100">
          <div className="font-semibold mb-3">Почасовая активность</div>
          <div className="flex items-end gap-1 h-28">
            {detail.hours.map((h) => {
              const max = Math.max(...detail.hours.map((x) => x.count));
              const heightPct = max > 0 ? (h.count / max) * 100 : 0;
              return (
                <div key={h.hour} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div
                    className="w-full bg-brand-500 rounded-t"
                    style={{ height: `${heightPct}%` }}
                    title={`${h.hour}:00 — ${h.count} заказов, ${formatRub(h.revenue)}`}
                  />
                  <span className="text-[10px] text-slate-400">{h.hour}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Заказы смены */}
      <div className="card p-4 border border-slate-100">
        <div className="font-semibold mb-3 flex items-center gap-2">
          <ClipboardList size={14} /> Все заказы смены ({detail.orders.length})
        </div>
        {detail.orders.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">Заказов нет</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase">
                  <th className="text-left px-2 py-2">№</th>
                  <th className="text-left px-2 py-2">Время</th>
                  <th className="text-left px-2 py-2">Статус</th>
                  <th className="text-left px-2 py-2">Источник</th>
                  <th className="text-left px-2 py-2">Стол</th>
                  <th className="text-left px-2 py-2">Клиент</th>
                  <th className="text-right px-2 py-2">Скидка</th>
                  <th className="text-right px-2 py-2">Итого</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.orders.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50">
                    <td className="px-2 py-2 font-medium">#{o.number}</td>
                    <td className="px-2 py-2 text-slate-500">
                      {new Date(o.createdAt).toLocaleTimeString('ru-RU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full border ${statusColor[o.status]}`}>
                        {orderStatusLabels[o.status] ?? o.status}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {o.source === 'APP' ? 'Приложение' : 'Касса'}
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {o.table ? `Стол ${o.table.number}` : '—'}
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {o.client?.name ?? o.client?.phone ?? '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {o.discount > 0 ? formatRub(o.discount) : '—'}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold">{formatRub(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {expenseOpen && (
        <ExpenseModal
          expense={null}
          categories={categories ?? []}
          presetShift={{ id: detail.id, number: detail.number, status: detail.status, closedAt: detail.closedAt }}
          onClose={() => setExpenseOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Действия с отчётом ─────────────────────────────────────

/** YYYY-MM-DD открытия смены в часовом поясе заведения (fallback — браузера). */
function shiftDateStamp(detail: ShiftDetail): string {
  const d = new Date(detail.openedAt);
  if (Number.isNaN(d.getTime())) return 'unknown-date';
  const tz = detail.establishment?.timezone;
  try {
    // en-CA даёт ISO-подобный формат YYYY-MM-DD без ручной сборки.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function ReportActions({ detail }: { detail: ShiftDetail }) {
  const [emailOpen, setEmailOpen] = useState(false);
  // Имя файла латиницей (п.19): кириллица в Content-Disposition/почтовых
  // вложениях у части клиентов превращается в «_____.pdf». Дата — по зоне
  // заведения, а не UTC: ночная смена не должна «уезжать» на прошлый день.
  const fileName = `smena-${detail.number}-${shiftDateStamp(detail)}.pdf`;

  const onPrint = async () => {
    const blob = await pdf(<ShiftReportPdf shift={detail} />).toBlob();
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      w.addEventListener('load', () => {
        try { w.print(); } catch { /* ignore */ }
      });
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PDFDownloadLink
        document={<ShiftReportPdf shift={detail} />}
        fileName={fileName}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm"
      >
        {({ loading }) => (
          <>
            <Download size={14} />
            {loading ? 'Готовим PDF…' : 'Скачать PDF'}
          </>
        )}
      </PDFDownloadLink>
      <button
        onClick={onPrint}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-sm"
      >
        <Printer size={14} /> Печать
      </button>
      <button
        onClick={() => setEmailOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-sm"
      >
        <Mail size={14} /> Email
      </button>
      {emailOpen && <EmailModal detail={detail} fileName={fileName} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

function EmailModal({
  detail,
  fileName,
  onClose,
}: {
  detail: ShiftDetail;
  fileName: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(detail.cashier?.email ?? '');
  const subject = `Отчёт по смене #${detail.number}`;
  const body = [
    `Отчёт по смене #${detail.number}`,
    `Заведение: ${detail.establishment?.name ?? 'Resstro SAS'}`,
    `Открыта: ${formatDate(detail.openedAt)}`,
    `Закрыта: ${detail.closedAt ? formatDate(detail.closedAt) : '—'}`,
    `Кассир: ${detail.cashierName ?? detail.cashier?.name ?? detail.cashier?.phone ?? '—'}`,
    ``,
    `Выручка: ${formatRub(detail.totals.revenue)}`,
    `Заказов: ${detail.totals.ordersCount}`,
    `Средний чек: ${formatRub(detail.totals.avgCheck)}`,
    `Отменено: ${detail.totals.cancelledCount}`,
    `Скидки: ${formatRub(detail.totals.discountTotal)}`,
    ``,
    `PDF-отчёт во вложении: ${fileName}`,
  ].join('\n');

  const sendMailto = () => {
    const href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    onClose();
  };

  const downloadAndOpenMail = async () => {
    const blob = await pdf(<ShiftReportPdf shift={detail} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    sendMailto();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FileText size={16} />
            <h3 className="text-base font-semibold">Отправить отчёт по email</h3>
          </div>
          <button className="p-1 rounded hover:bg-slate-100" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-slate-500">
            Скачаем PDF и откроем письмо в вашем почтовом клиенте с заполненной темой и телом. Вложите файл из «Загрузок».
          </p>
          <label className="block">
            <span className="text-xs text-slate-500">Email получателя</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@company.ru"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
            />
          </label>
          <div className="text-xs text-slate-400">
            Тема: {subject}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button
            className="px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-sm"
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            disabled={!email}
            onClick={downloadAndOpenMail}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            <Mail size={14} /> Скачать PDF и открыть письмо
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── UI-хелперы ─────────────────────────────────────────────

type Tone = 'brand' | 'success' | 'warning' | 'slate' | 'rose';
const toneBadge: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-700',
  rose: 'bg-rose-50 text-rose-700',
};
function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone: Tone }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded inline-block ${toneBadge[tone]}`}>
        {label}
      </div>
      <div className="text-lg font-semibold mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function CashRow({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'pos' | 'neg' | 'warn';
}) {
  const toneClass =
    tone === 'pos'
      ? 'text-emerald-700'
      : tone === 'neg'
        ? 'text-rose-700'
        : tone === 'warn'
          ? 'text-amber-700'
          : 'text-slate-900';
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`${strong ? 'font-semibold' : 'font-medium'} ${toneClass} tabular-nums`}>
        {value}
      </div>
    </div>
  );
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
}
